import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { useCurrentUser } from '@ingenico/react-section-utils';
import { CardCustom } from '@ingenico/react-ui/card-custom';
import { CoreButton } from '@ingenico/react-ui/core-button';
import { CoreStatus } from '@ingenico/react-ui/core-status';
import { CoreTypography, CoreTypographyVariant } from '@ingenico/react-ui/core-typography';
import { DateTime } from '@ingenico/react-ui/date-time';
import { DeviceImageMapper } from '@ingenico/react-ui/device-image-mapper';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Divider, Tooltip } from '@mui/material';
import classNames from 'classnames';
import { capitalize } from 'lodash-es';
import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeviceManagementCapability } from '../../hooks/useDeviceManagementCapability.hook';
import { dashString } from '../../utils/dashString.util';
import { LogicalDeviceConnectivityInfoGroup } from '../logical-device-connectivity-info-group/LogicalDeviceConnectivityInfoGroup.ctrl';
import { LogicalDeviceHardwareStatusCharts } from '../logical-device-hardware-status-charts/LogicalDeviceHardwareStatusCharts.ctrl';
import type { LogicalDeviceSummaryCardProps } from './logicalDeviceSummaryCard.model';
import styles from './logicalDeviceSummaryCard.module.scss';
import {
  checkShouldDisplayInfo,
  checkShouldShowConnectivityStatus,
  getConnectivityInfoForGroup,
  getConnectivityStatusLabel,
  getConnectivityStatusType,
  getCurrentStatusLabel,
  getMobileNetworkIconColor,
  getScreenStatusLabel,
  getScreenStatusType,
  getSignalStrength,
  shouldDisplayScreenLockingStatus,
} from './logicalDeviceSummaryCard.util';

export function LogicalDeviceSummaryCard({
  connectivity,
  general,
  onClickRefresh,
  hardware,
  isLoadingRefreshButton,
  componentsClassNames: customClassNames,
  hideInfo,
  displayEmptyAsDash,
  hiddenConnectivity,
  hideConnectivityStatusChip,
  image,
}: LogicalDeviceSummaryCardProps): ReactNode {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.LOGICAL_DEVICE, ALL_LNGS_NAMESPACES.COMMON]);
  const isDeviceBlockFeatureEnabled = useDeviceManagementCapability('logicalDevice.isDeviceBlockFeatureEnabled');

  const connectivityInfoGroup = useMemo(
    () => [
      ...getConnectivityInfoForGroup({ connectivity, key: 'SSID', icon: 'wifi', hiddenConnectivity }),
      ...getConnectivityInfoForGroup({ connectivity, key: 'bluetooth', icon: 'bluetooth', hiddenConnectivity }),
      ...getConnectivityInfoForGroup({ connectivity, key: 'ethernet', icon: 'setting-ethernet', hiddenConnectivity }),
      ...getConnectivityInfoForGroup({
        connectivity,
        key: 'mobileNetwork',
        icon: getSignalStrength(connectivity?.mobileNetwork?.strength),
        overrideColor: getMobileNetworkIconColor(connectivity?.mobileNetwork),
        hiddenConnectivity,
      }),
      ...getConnectivityInfoForGroup({ connectivity, key: 'SIM', icon: 'sim-card', hiddenConnectivity }),
    ],
    [connectivity, hiddenConnectivity],
  );

  const screenLockingStatusInfo = useMemo(() => {
    const currentScreenLockingStatus = general.screenLockingStatus;

    return {
      label: t(getScreenStatusLabel(currentScreenLockingStatus)),
      shouldDisplay: shouldDisplayScreenLockingStatus(currentScreenLockingStatus) && isDeviceBlockFeatureEnabled,
      status: getScreenStatusType(currentScreenLockingStatus),
    };
  }, [general.screenLockingStatus, isDeviceBlockFeatureEnabled, t]);

  const currentUser = useCurrentUser();

  const displayLastSyncDate = useCallback(
    (lastSyncDate?: string) => {
      return (
        <>
          <CoreTypography variant={CoreTypographyVariant.Body02Regular}>{t('deviceProperties.date')}</CoreTypography>
          {lastSyncDate ? (
            <DateTime
              customClassName={styles.lastSyncDate}
              instant={lastSyncDate}
              timezone={currentUser.zoneInfo}
              userDateTimeFormat={currentUser.dateTimeFormat}
            />
          ) : (
            dashString()
          )}
        </>
      );
    },
    [currentUser, t],
  );

  return (
    <CardCustom className={classNames(styles.card, customClassNames?.card)}>
      <div className={classNames(styles.hardwareInfoContainer, customClassNames?.hardwareContainer)}>
        <div className={classNames(styles.imageContainer, customClassNames?.imageContainer)}>
          <DeviceImageMapper
            customClass={classNames(styles.image, customClassNames?.image)}
            image={image ?? general.model}
          />
        </div>
        <div className={classNames(styles.hardware, customClassNames?.hardwareInfoSectionContainer)}>
          <div className={styles.identificationContainer}>
            <CoreTypography variant={CoreTypographyVariant.Headline02Bold}>{general.name}</CoreTypography>
            <CoreTypography variant={CoreTypographyVariant.Body02Regular}>
              {`${dashString(general.manufacturer && capitalize(general.manufacturer))} | ${dashString(general.serialNumber)} | ${t('logicalDevice.referenceAbbreviation')} ${dashString(general.reference)}`}
            </CoreTypography>
            <div className={styles.lastSyncContainer}>
              {displayLastSyncDate(general.lastSyncDate)}
              <Tooltip title={t('logicalDevice:deviceProperties.tooltip.lastSeen')}>
                <InfoIcon className={styles.tooltipIcon} />
              </Tooltip>
              {onClickRefresh && (
                <CoreButton
                  loading={isLoadingRefreshButton}
                  onClick={onClickRefresh}
                  size="small"
                  startIcon={<RefreshIcon />}
                  variant="text"
                >
                  {t('button.refresh', { ns: ALL_LNGS_NAMESPACES.COMMON })}
                </CoreButton>
              )}
            </div>
          </div>
          <div className={styles.statusContainer}>
            {!hideConnectivityStatusChip && checkShouldShowConnectivityStatus(general) && (
              <div className={styles.deviceStatus}>
                <CoreStatus
                  label={t(getConnectivityStatusLabel(general.isConnected))}
                  status={getConnectivityStatusType(general.isConnected)}
                />
                {general.connectivityStatus && checkShouldDisplayInfo('connectivityStatus', hideInfo) && (
                  <CoreTypography customClassName={styles.statusSubtitle} variant={CoreTypographyVariant.Body02Regular}>
                    {t(getCurrentStatusLabel(general.connectivityStatus))}
                  </CoreTypography>
                )}
              </div>
            )}
            {screenLockingStatusInfo.shouldDisplay ? (
              <CoreStatus label={screenLockingStatusInfo.label} status={screenLockingStatusInfo.status} />
            ) : undefined}
          </div>
          <LogicalDeviceHardwareStatusCharts
            componentsClassNames={{
              container: customClassNames?.hardwareChartsContainer,
              itemContainer: customClassNames?.hardwareChartsItemContainer,
            }}
            displayEmptyAsDash={displayEmptyAsDash}
            hardware={hardware}
          />
        </div>
      </div>
      <Divider className={classNames(styles.separator, customClassNames?.divider)} />
      <LogicalDeviceConnectivityInfoGroup
        componentsClassNames={{
          item: classNames(styles.connectivity__item, customClassNames?.connectivityItem),
          container: classNames(styles.connectivity__container, customClassNames?.connectivityContainer),
          divider: classNames(styles.connectivity__divider, customClassNames?.connectivityDivider),
        }}
        group={connectivityInfoGroup}
      />
    </CardCustom>
  );
}
